import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Moon, Trash2 } from "lucide-react";
import { PageHeader, Stat } from "./Sugar";

function diffHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let s = sh * 60 + sm; let e = eh * 60 + em;
  if (e <= s) e += 24 * 60;
  return +((e - s) / 60).toFixed(1);
}

export default function Sleep() {
  const [logs, setLogs] = useState([]);
  const [sleepTime, setSleepTime] = useState("23:00");
  const [wakeTime, setWakeTime] = useState("07:00");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => { try { const { data } = await api.get("/sleep"); setLogs(data); } catch {} };
  useEffect(() => { load(); }, []);

  const hours = diffHours(sleepTime, wakeTime);

  const add = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await api.post("/sleep", { sleep_time: sleepTime, wake_time: wakeTime, hours, date }); await load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/sleep/${id}`); await load(); };

  const avg = logs.length ? +(logs.reduce((a, l) => a + l.hours, 0) / logs.length).toFixed(1) : 0;
  const sleepScore = (h) => {
    if (h >= 7 && h <= 9) return { l: "Great", c: "emerald" };
    if (h >= 6) return { l: "Okay", c: "amber" };
    return { l: "Low", c: "rose" };
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="sleep-page">
      <PageHeader icon={Moon} title="Sleep" subtitle="Track when you sleep and how long" accent="violet" />

      <div className="grid grid-cols-2 gap-4 mb-6 stagger">
        <Stat label="Last night" value={logs[0]?.hours || "—"} unit="hours" accent="blue" />
        <Stat label="Average" value={avg || "—"} unit="hours" accent="emerald" />
      </div>

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="sleep-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</Label>
          <Input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="sleep-date" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sleep time</Label>
          <Input type="time" value={sleepTime} onChange={(e) => setSleepTime(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="sleep-start" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Wake time</Label>
          <Input type="time" value={wakeTime} onChange={(e) => setWakeTime(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="sleep-end" />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="sleep-submit">{loading ? "Saving…" : `Add (${hours}h)`}</Button>
        </div>
        {error && <div className="sm:col-span-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="sleep-error">{error}</div>}
      </form>

      <h3 className="font-heading font-semibold text-slate-800 mb-3">Recent</h3>
      <div className="space-y-2" data-testid="sleep-list">
        {logs.length === 0 && <div className="text-sm text-slate-400">No sleep logs yet.</div>}
        {logs.map(l => {
          const s = sleepScore(l.hours);
          return (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">{l.hours} hours</div>
                <div className="text-xs text-slate-500">{l.sleep_time} → {l.wake_time} • {l.date || new Date(l.created_at).toLocaleDateString()}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-${s.c}-50 text-${s.c}-700 border border-${s.c}-200`}>{s.l}</span>
                <button onClick={() => del(l.id)} data-testid={`sleep-del-${l.id}`} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
